import React from "react";
import { Box, CircularProgress, DialogActions, FormHelperText, IconButton, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import enzaLocale from "date-fns/locale/en-ZA";
import FeatherIcon from "feather-icons-react";
import { Formik } from "formik";
import moment from "moment";
import { useSnackbar } from "../../components/toast/index.jsx";
import { DraggableDialog } from "../../consts.js";
import { CAMPAIGN_DIRECTION, CAMPAIGN_TYPE_OPTIONS } from "../../helpers/constant.js";
import { CAMPAIGN_UPDATE_FAILED, CAMPAIGN_UPDATE_SUCCESS } from "../../helpers/messages.js";
import Utils from "../../utils.js";
import { EditCampaignSchema } from "./validator.js";
import AppSelect from "../../components/common-form/select-input/index.jsx";

function EditCampaignComponent({ open, onClose, companyData, trackersData, allCampaigns }) {
    const showSnackbar = useSnackbar();
    const [trackers, setTrackers] = React.useState([]);
    const [inTrackers, setInTrackers] = React.useState([]);

    async function getCompanyName() {
        if (companyData.trackers) {
            let trk = JSON.parse(companyData.trackers);
            trk = trk.filter(
                (value, index, self) =>
                    index === self.findIndex((t) => t.name === value.name)
            );
            setInTrackers(trk);
        }
        setTrackers(trackersData);
    }
    
    React.useEffect(() => getCompanyName(), [companyData]);

    const initialValues = {
        campaignName: companyData.name,
        type: companyData.type,
        direction: companyData.direction,
        startDate: moment(companyData.startdate).format("YYYY-MM-DD"),
        endDate: moment(companyData.enddate).format("YYYY-MM-DD")
    }

    /**
     * Submit Method to create a campaign
     */
    const submitForm = async (values, { setSubmitting }) => {
        const campaignsExist = allCampaigns.some(s => (s.name.trim().toLowerCase() === values.campaignName.trim().toLowerCase() && companyData.id !== s.id));
        if(campaignsExist) {
            showSnackbar(`Campaign "${values.campaignName}" already exists.`, "error");
            setSubmitting(false);
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                id: companyData.id,
                name: values.campaignName.trim(),
                startdate: values.startDate,
                enddate: values.endDate,
                type: values.type,
                direction: values.direction,
                trackers: JSON.stringify(inTrackers),
            };
            await Utils.genericMutation("updateCampaign", payload);
            setSubmitting(false);
            onClose(true);
            showSnackbar(CAMPAIGN_UPDATE_SUCCESS);
        } catch (error) {
            console.log(error)
            setSubmitting(false);
            showSnackbar(CAMPAIGN_UPDATE_FAILED, "error")
        }
    }

    const handleClose = (event, reason) => {
        // Prevent closing the dialog on backdrop click or escape key press
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
        }
        event.stopPropagation();
        onClose(null);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="draggable-dialog-title"
            scroll="paper"
            disableEscapeKeyDown={true}
            fullWidth={true}
            maxWidth="sm"
            PaperComponent={DraggableDialog}
            onClick={(e) => e.stopPropagation()}
        >
            <DialogTitle
                id="draggable-dialog-title"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                gap={1}
            >
                Edit Campaign
                <IconButton
                    className="draggable-cancel"
                    size="small"
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        color: theme.palette.grey[400],
                        marginRight: "-.5rem",
                    })}
                >
                    <FeatherIcon icon="x" />
                </IconButton>
            </DialogTitle>

            <Formik
                enableReinitialize
                validationSchema={EditCampaignSchema}
                initialValues={initialValues}
                onSubmit={(values, actions) => {
                    submitForm(values, actions);
                }}
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    setFieldValue,
                    setFieldTouched,
                    values,
                    errors,
                    touched,
                    isSubmitting,
                }) => (
                    <Box
                        component="form"
                        display="flex"
                        flexDirection="column"
                        flexGrow={1}
                        overflow="auto"
                        noValidate
                        onSubmit={handleSubmit}
                    >
                        <DialogContent dividers sx={{ paddingY: 3 }}>
                            <TextField
                                id="outlined-basic"
                                variant="outlined"
                                fullWidth
                                label="Campaign Name"
                                required
                                name={"campaignName"}
                                value={values.campaignName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={touched.campaignName && !!errors.campaignName}
                                helperText={touched.campaignName ? errors.campaignName : ''}
                            />
                            <AppSelect
                                label="Type"
                                name={"type"}
                                options={CAMPAIGN_TYPE_OPTIONS}
                                value={values.type}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.type}
                                error={errors.type}
                                disabled={isSubmitting}
                                validationSchema={EditCampaignSchema}
                                MenuProps={{ width: "100%" }}
                                sx={{ marginTop: 3 }}
                            />
                            <AppSelect
                                label="Call Direction"
                                name={"direction"}
                                options={CAMPAIGN_DIRECTION}
                                value={values.direction}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                touched={touched.direction}
                                error={errors.direction}
                                disabled={isSubmitting}
                                validationSchema={EditCampaignSchema}
                                MenuProps={{ width: "100%" }}
                                sx={{ marginTop: 3 }}
                            />
                            <Stack flexDirection="row" gap={{ xs: 1, sm: 2.5 }} sx={{ marginTop: 2.5, width: "100%" }}>
                                <LocalizationProvider
                                    dateAdapter={AdapterDateFns}
                                    locale={enzaLocale}
                                >
                                    <div style={{ width: "100%" }}>
                                        <DatePicker
                                            label="Start Date"
                                            name={"startDate"}
                                            slotProps={{ textField: { required: true } }}
                                            value={moment(values.startDate).toDate()}
                                            onChange={(date) => {
                                                const startDate = moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null;
                                                setFieldValue('startDate', startDate)
                                            }}
                                            onClose={() => setFieldTouched('startDate', true)}
                                            onError={() => setFieldTouched('startDate', true)}
                                            error={touched.startDate && !!errors.startDate}
                                            renderInput={(params) => <TextField {...params} />}
                                            sx={{ width: "100%" }}
                                        />
                                        {(touched.startDate && !!errors.startDate) ? (
                                            <FormHelperText error={true}>{errors.startDate}</FormHelperText>
                                        ) : ''}
                                    </div>
                                    <div style={{ width: "100%" }}>
                                        <DatePicker
                                            label="End Date"
                                            name={"endDate"}
                                            slotProps={{ textField: { required: true } }}
                                            minDate={values.startDate ? moment(values.startDate).toDate() : null}
                                            value={moment(values.endDate).toDate()}
                                            onChange={(date) => {
                                                const endDate = moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : null;
                                                setFieldValue('endDate', endDate)
                                            }}
                                            onClose={() => setFieldTouched('endDate', true)}
                                            onError={() => setFieldTouched('endDate', true)}
                                            error={touched.endDate && !!errors.endDate}
                                            renderInput={(params) => <TextField {...params} />}
                                            sx={{ width: "100%" }}
                                        />
                                        {(touched.endDate && !!errors.endDate) ? (
                                            <FormHelperText error={true}>{errors.endDate}</FormHelperText>
                                        ) : ''}
                                    </div>
                                </LocalizationProvider>
                            </Stack>
                            {true ? null : (
                                <FormControl fullWidth sx={{ marginTop: 3 }}>
                                    <InputLabel id="demo-simple-select-label">
                                        Add Insight Sets
                                    </InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={inTrackers}
                                        onChange={(z) => {
                                            let trk = Object.assign([], z.target.value);
                                            trk = trk.filter(
                                                (value, index, self) =>
                                                    index === self.findIndex((t) => t.name === value.name)
                                            );
                                            setInTrackers(trk);
                                        }}
                                        onSelect={(e) => {
                                            console.log(e);
                                        }}
                                        label="Add Insight Sets"
                                        multiple
                                        minrows={3}
                                        multiline
                                        renderValue={(selected) => {

                                            return selected.map((j, ji) => <div key={ji}>{j.name}</div>);
                                        }}
                                        MenuProps={{ width: "100%" }}
                                    >
                                        {trackers.map((z, zid) => (
                                            <MenuItem value={z} key={zid}>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    <Checkbox
                                                        onClick={(e) => {
                                                            if (e.target.checked === false) {
                                                                let trk = Object.assign([], inTrackers);
                                                                trk = trk.filter((w) => w.name !== z.name);
                                                                setInTrackers(trk);
                                                            }
                                                        }}
                                                        checked={
                                                            inTrackers.filter((l) => l.name === z.name).length > 0
                                                        }
                                                    />
                                                    <ListItemText primary={z.name} />
                                                </div>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                variant="outlined"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <CircularProgress color="inherit" size={24} /> : "Update"}
                            </Button>
                        </DialogActions>
                    </Box>
                )}
            </Formik>
        </Dialog>
    );
}

export default EditCampaignComponent;
